import React, {useEffect, useState} from "react";
import {APIv1} from "../../../api";
import {Spin} from "antd";
import {Share2} from 'lucide-react';
import {StatusIcon, LogsStatusIcon} from "../../../utils/statusIcons";
import {useParams} from "react-router-dom";
import {extractDateBySecond} from "../../../utils";
import {ConvertLogsPaymentProvider} from "../../../utils/logsUtils";
import {useTranslation} from "react-i18next";
import {DownloadPDF} from "../../../utils/generatorFiles/generatePDF";
import LogsContentSections from "../../../components/PaymentLogs/LogsContentSections";
import "../../../styles/BaseLogsStyle.scss"

const LogsDetail = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const [userData, setUserData] = useState({});
    const [logsData, setLogsData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'));
        if (items) {
            setUserData(items);
        }
    }, []); // load once on mount

    useEffect(() => {
        const fetchLogs = async () => {
            if (!userData.token) return;

            setLoading(true);
            try {
                const {data} = await APIv1(`payment/logs/retrieve/${id}`, {
                    headers: {
                        Authorization: `Token ${userData.token}`,
                    }
                });

                const log = data.message;
                const logData = {
                    id: log.id,
                    key: log.id,
                    inn: log.inn,
                    deviceSerial: log.device_serial === 'None' ? '-' : log.device_serial,
                    transactionId: log.transaction_id === 'None' ? '-' : log.transaction_id,
                    paymentId: log.payment_id,
                    amount: log.amount,
                    fiscalUrl: log.fiscal_url,
                    phoneNumber: log.phone,
                    isSuccess: log.is_success,
                    status: log.status,
                    logType: ConvertLogsPaymentProvider(log.log_type),
                    createdDate: extractDateBySecond(log.created_date)
                };

                setLogsData(logData);
            } catch (error) {
                console.log('Something went wrong:', error);
                setLogsData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [userData.token, id]); // Only trigger when token and id are both available

    if (loading) {
        return (
            <div className="content_container" style={{display: 'flex', justifyContent: 'center', padding: '48px 0'}}>
                <Spin/>
            </div>
        );
    }

    if (!logsData) {
        return <div>Log not found</div>;
    }

    return (
        <div className="content_container">
            <div className="payment-log__header">
                <div className="payment-log__title">
                    <h1 className="payment-log__main-title">{t("pages.logs.detailColumns.title")} #{logsData.key}</h1>
                </div>
                <div className="payment-log__action-buttons">
                    <DownloadPDF logsData={logsData} type={"logs"}/>
                    <Share2 className="payment-log__action-buttons--inactive"/>
                </div>
            </div>

            <div className="payment-log__grid">
                <div className="payment-log__field">
                    <span className="payment-log__label">{t("common.inn")}:</span>
                    <span className="payment-log__value">{logsData.inn}</span>
                </div>

                <div className="payment-log__field">
                    <span className="payment-log__label">{t("common.deviceSerial")}:</span>
                    <span className="payment-log__value">{logsData.deviceSerial}</span>
                </div>

                <div className="payment-log__field">
                    <span className="payment-log__label">{t("pages.logs.detailColumns.showcase3")}:</span>
                    <span className="payment-log__value">{logsData.transactionId || '-'}</span>
                </div>
            </div>

            <div className="payment-log__grid">
                <div className="payment-log__field">
                    <span className="payment-log__label">{t("pages.logs.detailColumns.showcase4")}:</span>
                    <span className="payment-log__value">{logsData.amount}</span>
                </div>

                <div className="payment-log__field">
                    <span className="payment-log__label">{t("pages.logs.detailColumns.showcase5")}:</span>
                    <span className="payment-log__value">{logsData.paymentId}</span>
                </div>

                <div className="payment-log__field">
                    <span className="payment-log__label">{t("pages.logs.detailColumns.showcase6")}:</span>
                    <span className="payment-log__value">{logsData.phoneNumber || '-'}</span>
                </div>
            </div>

            <LogsContentSections
                t={t}
                token={userData.token}
                logsData={logsData}
                onContentLoaded={(contentData) => setLogsData(prevLogsData => ({
                    ...prevLogsData,
                    ...contentData,
                }))}
            />

            <div className="payment-log__grid">
                <div className="payment-log__field">
                    <span className="payment-log__label">{t("pages.logs.detailColumns.showcase7")}:</span>
                    <span className="payment-log__value">{logsData.logType}</span>
                </div>

                <div className="payment-log__field">
                    <span className="payment-log__label">{t("pages.logs.detailColumns.showcase8")}:</span>
                    <span className="payment-log__value">{logsData.createdDate}</span>
                </div>

                <div className="payment-log__field payment-log__field--row">
                    <span className="payment-log__label">{t("common.status")}:</span>
                    <div className="payment-log__status">
                        <StatusIcon status={logsData.isSuccess}/>
                        <span className="payment-log__status-text">{logsData.isSuccess}</span>
                    </div>
                </div>

                <div className="payment-log__field payment-log__field--row">
                    <span className="payment-log__label">{t("pages.logs.detailColumns.showcase10")}:</span>
                    <div className="payment-log__status">
                        <LogsStatusIcon size={22} status={logsData.status}/>
                        <span className="payment-log__status-text">{logsData.status}</span>
                    </div>
                </div>
            </div>
            <div className="payment-log__grid">
                <div className="payment-log__field">
                    <span className="payment-log__label">{t("pages.logs.detailColumns.showcase11")}:</span>
                    <a href={logsData.fiscalUrl} className="payment-log__value" target="_blank"
                       rel="noreferrer">{logsData.fiscalUrl}</a>
                </div>
            </div>
        </div>
    );
};


export default LogsDetail;
